// ============================================
// Gemini AI Service - Domain Analysis & Chat
// ============================================

import { GoogleGenAI, Type } from "@google/genai";
import { AIAnalysisResult } from "../types";

export class GeminiService {
  private client: GoogleGenAI | null = null;
  private apiKey: string;

  constructor(apiKey: string) {
    this.apiKey = apiKey;
    if (this.apiKey) {
      this.client = new GoogleGenAI({ apiKey: this.apiKey });
    }
  }

  updateApiKey(key: string) {
    this.apiKey = key;
    if (this.apiKey) {
      this.client = new GoogleGenAI({ apiKey: this.apiKey });
    } else {
      this.client = null;
    }
  }

  isConfigured(): boolean {
    return !!this.client;
  }

  /**
   * Quick Check - Fast screening using Flash Lite
   * Returns YES/NO/MAYBE with brief reasoning
   */
  async quickCheck(domainName: string): Promise<string> {
    if (!this.client) return "AI Not Configured";

    try {
      const response = await this.client.models.generateContent({
        model: "gemini-2.0-flash-lite",
        contents: `Evaluate the domain name "${domainName}" for a PERSONAL INJURY LAW domain investor.

Consider:
- Relevance to personal injury law, accident attorneys, DUI defense, medical malpractice, wrongful death
- Value for law firm lead generation and attorney marketing
- Memorability and brandability for legal services
- Geographic targeting potential (city + practice area)
- Potential trademark issues

Answer in one sentence starting with YES (strong buy), MAYBE (consider), or NO (pass).`,
      });
      return response.text?.trim() || "Unable to analyze";
    } catch (error) {
      console.error("Quick check error:", error);
      return "Analysis error - check API key";
    }
  }

  /**
   * Deep Analysis - Comprehensive domain valuation
   * Uses Flash with search grounding for market data
   */
  async analyzeDomain(domain: string, price: number): Promise<AIAnalysisResult> {
    if (!this.client) {
      throw new Error("AI Not Configured - add Google API key in settings");
    }

    const prompt = `
Analyze this domain for a PERSONAL INJURY LAW domain investor:

Domain: ${domain}
Current Auction Price: $${price}

Provide analysis covering:
1. Estimated resale value range for personal injury law firms
2. Your verdict: "Top Pick", "Backorder", "Hand Register", or "Pass"
3. Commercial analysis - who would buy this? (PI attorneys, accident lawyers, DUI defense, medical malpractice firms)
4. Lead generation potential - how many leads/month could this domain generate?
5. SEO value - existing backlinks, domain age, search rankings
6. Geographic or practice area targeting value

Target buyers: Personal injury law firms, accident attorney practices, legal lead generation companies, attorney marketing agencies.
`;

    try {
      const response = await this.client.models.generateContent({
        model: "gemini-2.0-flash",
        contents: prompt,
        config: {
          tools: [{ googleSearch: {} }],
          responseMimeType: "application/json",
          responseSchema: {
            type: Type.OBJECT,
            properties: {
              valuation: {
                type: Type.STRING,
                description: "Estimated value range (e.g., '$2,000 - $5,000')",
              },
              verdict: {
                type: Type.STRING,
                enum: ["Top Pick", "Backorder", "Hand Register", "Pass"],
              },
              analysis: {
                type: Type.STRING,
                description: "2-3 sentence commercial analysis",
              },
              seo_notes: {
                type: Type.STRING,
                description: "SEO history and trademark risk notes",
              },
              legal_relevance: {
                type: Type.STRING,
                description: "Relevance to legal industry (High/Medium/Low with explanation)",
              },
            },
            required: ["valuation", "verdict", "analysis", "seo_notes", "legal_relevance"],
          },
        },
      });

      const text = response.text;
      if (!text) throw new Error("Empty response from AI");

      return JSON.parse(text) as AIAnalysisResult;
    } catch (error) {
      console.error("Domain analysis error:", error);
      return {
        valuation: "Unable to determine",
        verdict: "Pass",
        analysis: "Analysis failed. Please try again.",
        seo_notes: "Error occurred during analysis",
        legal_relevance: "Unknown",
      };
    }
  }

  /**
   * Batch Quick Analysis - Screen multiple domains quickly
   */
  async batchQuickCheck(
    domains: string[]
  ): Promise<Map<string, { viable: boolean; reason: string }>> {
    if (!this.client) {
      throw new Error("AI Not Configured");
    }

    const results = new Map<string, { viable: boolean; reason: string }>();

    // Process in batches of 10
    const batchSize = 10;
    for (let i = 0; i < domains.length; i += batchSize) {
      const batch = domains.slice(i, i + batchSize);

      try {
        const response = await this.client.models.generateContent({
          model: "gemini-2.0-flash-lite",
          contents: `Rate these domains for LEGAL industry investment. For each, give a 1-word verdict (YES/MAYBE/NO) and 5-word reason.

Domains:
${batch.map((d, idx) => `${idx + 1}. ${d}`).join("\n")}

Format each as: DOMAIN: VERDICT - reason`,
        });

        const text = response.text || "";
        const lines = text.split("\n").filter((l) => l.trim());

        for (const line of lines) {
          const match = line.match(/([a-z0-9.-]+\.[a-z]+):\s*(YES|MAYBE|NO)\s*-?\s*(.+)?/i);
          if (match) {
            results.set(match[1].toLowerCase(), {
              viable: match[2].toUpperCase() === "YES",
              reason: match[3]?.trim() || "",
            });
          }
        }
      } catch (error) {
        console.error("Batch analysis error:", error);
        batch.forEach((d) =>
          results.set(d, { viable: false, reason: "Analysis failed" })
        );
      }
    }

    return results;
  }

  /**
   * Chat - Interactive domain consulting
   */
  async chatMessage(
    history: { role: string; parts: { text: string }[] }[],
    message: string
  ): Promise<string> {
    if (!this.client) {
      return "Please configure your Google API key in settings to use the AI assistant.";
    }

    try {
      const chat = this.client.chats.create({
        model: "gemini-2.0-flash",
        history: history,
        config: {
          systemInstruction: `You are an expert Domain Name Investor specializing in premium domain acquisition through DropCatch.

Your expertise includes:
- Valuing premium domain names across all industries
- Understanding domain aftermarket trends and pricing
- Geographic + keyword domain combinations (e.g., "DallasServices.com")
- Brandable, keyword-rich, and short domain valuations
- SEO value assessment for domain investments
- Trademark considerations for domain purchases
- Lead generation potential across various industries

Help users evaluate domains, suggest bidding strategies, identify high-value opportunities, and provide market insights on domain sales.

Keep responses concise but informative. Reference recent domain sales when helpful.`,
        },
      });

      const result = await chat.sendMessage({ message });
      return result.text || "I couldn't generate a response. Please try again.";
    } catch (error) {
      console.error("Chat error:", error);
      return "An error occurred. Please check your API key and try again.";
    }
  }

  /**
   * Generate domain suggestions based on keywords
   */
  async suggestDomains(keywords: string[]): Promise<string[]> {
    if (!this.client) {
      throw new Error("AI Not Configured");
    }

    try {
      const response = await this.client.models.generateContent({
        model: "gemini-2.0-flash",
        contents: `Generate 20 premium domain name ideas for PERSONAL INJURY LAW firms based on these keywords: ${keywords.join(", ")}

Requirements:
- .com extensions preferred (highest value for legal)
- Include geographic + practice area combos (e.g., "HoustonAccidentLawyer.com")
- Include practice-specific domains (e.g., "TruckAccidentSettlement.com")
- Easy to spell and pronounce over the phone
- Good for attorney marketing and lead generation
- Include some exact-match keyword domains

Focus areas: personal injury, car accidents, truck accidents, motorcycle accidents, DUI defense, wrongful death, medical malpractice, slip and fall, workers comp

Return ONLY the domain names, one per line, no numbering or explanations.`,
      });

      const text = response.text || "";
      return text
        .split("\n")
        .map((line) => line.trim().toLowerCase())
        .filter((line) => line && line.includes("."));
    } catch (error) {
      console.error("Domain suggestion error:", error);
      return [];
    }
  }
}
